import { LIBELLES_NATURE, type Nature, type Valeur } from '../../src/partage/nature-valeur.js';

/**
 * UN NOMBRE NE S'AFFICHE JAMAIS SANS SA NATURE (§11).
 *
 * ⚠️ CE COMPOSANT EXISTE POUR QU'IL SOIT IMPOSSIBLE D'AFFICHER UNE MÉDIANE
 * COMME S'IL S'AGISSAIT D'UN PRIX OBSERVÉ. Une mention « estimation » posée à
 * la main dans un écran se perd au premier composant recopié, et le nombre
 * continue de circuler sans elle. Ici, le nombre et sa nature sont le même
 * objet — on ne peut pas rendre l'un sans l'autre.
 *
 * ⚠️ ET « INDÉTERMINÉ » N'EST PAS UN TIRET DISCRET. C'est un état affiché, avec
 * son motif et son effectif. Rendre une case vide reviendrait à laisser le
 * lecteur y mettre ce qu'il espère — c'est-à-dire zéro, ou pire, la valeur
 * qu'il avait en tête en ouvrant la page.
 */

const CLASSE: Record<Nature, string> = {
  observee: 'nat-observee',
  calcul: 'nat-calcul',
  estimation: 'nat-estimation',
  projection: 'nat-projection',
  inconnu: 'nat-inconnu',
};

/** Format monétaire guinéen : pas de décimale, séparateur de milliers. */
export function montantGNF(v: number, devise: string | null = 'GNF'): string {
  return `${Math.round(v).toLocaleString('fr-FR')} ${devise ?? ''}`.trim();
}

export function Chiffre({
  v, unite, libelle, format,
}: {
  v: Valeur;
  unite?: string;
  libelle: string;
  format?: (n: number) => string;
}) {
  const nat = LIBELLES_NATURE[v.nature];
  const indetermine = v.valeur === null;

  return (
    <div className={`chiffre ${CLASSE[v.nature]}`}>
      <span className="chiffre-libelle">{libelle}</span>
      {indetermine ? (
        <strong className="chiffre-vide">indéterminé</strong>
      ) : (
        <strong className="chiffre-valeur">
          {format ? format(v.valeur as number) : (v.valeur as number).toLocaleString('fr-FR')}
          {unite && <em className="chiffre-unite">{unite}</em>}
        </strong>
      )}
      <span className="chiffre-nature" title={nat.long}>{nat.court}</span>
      {/* Le motif est TOUJOURS rendu, pas seulement quand ça échoue : c'est lui
          qui permet de contester un nombre au lieu de le subir. */}
      <span className="chiffre-base">{v.base}</span>
      <span className="chiffre-obs">
        {v.observations === 0 ? 'aucune observation' : `${v.observations} observation${v.observations > 1 ? 's' : ''}`}
      </span>
    </div>
  );
}

/**
 * L'écart entre ce qu'on a compté et ce qu'on a retenu.
 *
 * ⚠️ « médiane sur 6 » laisse croire qu'il n'y a que 6 transactions.
 * « 40 transactions, 6 retenues » est une information ; taire l'écart en
 * fabrique une fausse.
 */
export function Retenue({
  total, retenu, ecartees,
}: {
  total: number; retenu: number;
  ecartees: { nature_non_marchande: number; sans_prix: number; sans_surface: number };
}) {
  const perdues = total - retenu;
  if (total === 0) {
    return <p className="mention">Aucune transaction dans ce périmètre. Ce n'est pas « un marché à zéro » : c'est un registre sans observation.</p>;
  }
  if (perdues === 0) {
    return <p className="mention">{total} transaction(s), toutes exploitables pour le prix.</p>;
  }
  return (
    <p className="mention">
      <strong>{total}</strong> transaction(s) dans ce périmètre, <strong>{retenu}</strong> retenue(s) pour le calcul du prix.
      {' '}Écartées : {ecartees.nature_non_marchande} sans contrepartie par nature (donation, succession),
      {' '}{ecartees.sans_prix} vente(s) sans prix déclaré,
      {' '}{ecartees.sans_surface} sans surface de référence.
    </p>
  );
}
